import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

const SECTION_LABELS = {
  identity: "Identidad del negocio",
  navbar: "Navegación",
  hero: "Portada principal",
  spotlight: "Banner destacado",
  products: "Productos y servicios",
  featured: "Productos destacados",
  featuredProducts: "Productos destacados",
  categories: "Categorías",
  brands: "Marcas",
  about: "Sobre el negocio",
  highlights: "Diferenciales",
  contact: "Contacto",
  floatingWhatsapp: "WhatsApp flotante",
  footer: "Pie de página",
};

function clone(value) {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function getAtPath(source, path, fallback = "") {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => value?.[/^\d+$/.test(key) ? Number(key) : key], source) ?? fallback;
}

function setAtPath(source, path, value) {
  const next = clone(source);
  const keys = String(path || "").split(".").filter(Boolean);
  let cursor = next;
  keys.forEach((rawKey, index) => {
    const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    const nextKey = /^\d+$/.test(keys[index + 1]) ? Number(keys[index + 1]) : keys[index + 1];
    if (cursor[key] === undefined || cursor[key] === null) cursor[key] = typeof nextKey === "number" ? [] : {};
    cursor = cursor[key];
  });
  return next;
}

function Field({ label, path, form, onChange, multiline = false, minRows = 1, helperText = "", type = "text" }) {
  return (
    <TextField
      fullWidth
      label={label}
      type={type}
      value={getAtPath(form, path, "")}
      onChange={(event) => onChange(path, event.target.value)}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      helperText={helperText}
    />
  );
}

function VisibilitySwitch({ path, label, form, onChange }) {
  return (
    <FormControlLabel
      control={
        <Switch
          checked={getAtPath(form, path, true) !== false}
          onChange={(event) => onChange(path, event.target.checked)}
        />
      }
      label={label}
    />
  );
}

function IdentityFields(props) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}><Field {...props} label="Nombre del negocio" path="business.name" /></Grid>
      <Grid item xs={12}><Field {...props} label="Descripción breve" path="business.shortDescription" multiline minRows={3} /></Grid>
      <Grid item xs={12} sm={6}><Field {...props} label="WhatsApp" path="business.whatsapp" /></Grid>
      <Grid item xs={12} sm={6}><Field {...props} label="Teléfono" path="business.phone" /></Grid>
      <Grid item xs={12}><Field {...props} label="Correo electrónico" path="business.email" type="email" /></Grid>
    </Grid>
  );
}

function HeroFields(props) {
  return (
    <Stack spacing={2}>
      <VisibilitySwitch {...props} label="Mostrar portada" path="sections.showHero" />
      <Field {...props} label="Texto superior" path="hero.eyebrow" />
      <Field {...props} label="Título principal" path="hero.title" multiline minRows={2} />
      <Field {...props} label="Texto de apoyo" path="hero.subtitle" multiline minRows={3} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><Field {...props} label="Botón principal" path="hero.primaryCtaLabel" /></Grid>
        <Grid item xs={12} sm={6}><Field {...props} label="Destino del botón" path="hero.primaryCtaUrl" /></Grid>
        <Grid item xs={12} sm={6}><Field {...props} label="Botón secundario" path="hero.secondaryCtaLabel" /></Grid>
        <Grid item xs={12} sm={6}><Field {...props} label="Destino secundario" path="hero.secondaryCtaUrl" /></Grid>
      </Grid>
    </Stack>
  );
}

function CatalogFields({ activeSection, ...props }) {
  const block = activeSection === "categories" ? "categories" : activeSection === "brands" ? "brands" : "featured";
  const visibilityPath = activeSection === "categories"
    ? "sections.showCategories"
    : activeSection === "brands"
      ? "sections.showBrands"
      : "sections.showFeatured";
  return (
    <Stack spacing={2}>
      <VisibilitySwitch {...props} label="Mostrar esta sección" path={visibilityPath} />
      <Field {...props} label="Texto superior" path={`catalog.${block}.eyebrow`} />
      <Field {...props} label="Título" path={`catalog.${block}.title`} />
      <Field {...props} label="Descripción" path={`catalog.${block}.subtitle`} multiline minRows={3} />
      <Field {...props} label="Texto del botón" path={`catalog.${block}.ctaLabel`} />
    </Stack>
  );
}

function SpotlightFields(props) {
  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={<Switch checked={getAtPath(props.form, "catalog.spotlight.enabled", false) === true} onChange={(event) => props.onChange("catalog.spotlight.enabled", event.target.checked)} />}
        label="Activar banner destacado"
      />
      <VisibilitySwitch {...props} label="Permitir mostrar el banner" path="sections.showSpotlight" />
      <Field {...props} label="Texto superior" path="catalog.spotlight.eyebrow" />
      <Field {...props} label="Título" path="catalog.spotlight.title" />
      <Field {...props} label="Descripción" path="catalog.spotlight.description" multiline minRows={3} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><Field {...props} label="Texto del botón" path="catalog.spotlight.ctaLabel" /></Grid>
        <Grid item xs={12} sm={6}><Field {...props} label="Destino del botón" path="catalog.spotlight.ctaUrl" /></Grid>
      </Grid>
    </Stack>
  );
}

function AboutFields(props) {
  return (
    <Stack spacing={2}>
      <VisibilitySwitch {...props} label="Mostrar sección sobre el negocio" path="sections.showAbout" />
      <Field {...props} label="Texto superior" path="about.eyebrow" />
      <Field {...props} label="Título" path="about.title" />
      <Field {...props} label="Historia o descripción" path="about.description" multiline minRows={5} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><Field {...props} label="Texto del botón" path="about.ctaLabel" /></Grid>
        <Grid item xs={12} sm={6}><Field {...props} label="Destino del botón" path="about.ctaUrl" /></Grid>
      </Grid>
    </Stack>
  );
}

function HighlightsFields(props) {
  const items = Array.isArray(props.form.highlights) ? props.form.highlights : [];
  return (
    <Stack spacing={2}>
      <VisibilitySwitch {...props} label="Mostrar diferenciales" path="sections.showHighlights" />
      <Field {...props} label="Texto superior" path="highlightsIntro.eyebrow" />
      <Field {...props} label="Título" path="highlightsIntro.title" />
      <Field {...props} label="Descripción" path="highlightsIntro.subtitle" multiline minRows={3} />
      <Divider />
      {items.slice(0, 3).map((item, index) => (
        <Box key={item.id || index} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "rgba(25,36,47,.035)" }}>
          <Typography sx={{ mb: 1.2, fontWeight: 950 }}>Diferencial {index + 1}</Typography>
          <Stack spacing={1.4}>
            <Field {...props} label="Título" path={`highlights.${index}.title`} />
            <Field {...props} label="Descripción" path={`highlights.${index}.description`} multiline minRows={2} />
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function ContactFields(props) {
  return (
    <Stack spacing={2}>
      <VisibilitySwitch {...props} label="Mostrar sección de contacto" path="sections.showContact" />
      <Field {...props} label="Título" path="contact.title" />
      <Field {...props} label="Texto de apoyo" path="contact.subtitle" multiline minRows={3} />
      <Field {...props} label="Mensaje inicial de WhatsApp" path="contact.whatsappMessage" multiline minRows={3} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><Field {...props} label="Botón de WhatsApp" path="contact.whatsappCtaLabel" /></Grid>
        <Grid item xs={12} sm={6}><Field {...props} label="Botón de detalles" path="contact.detailsCtaLabel" /></Grid>
      </Grid>
    </Stack>
  );
}

function NavbarFields(props) {
  return (
    <Stack spacing={2}>
      <VisibilitySwitch {...props} label="Mostrar navegación" path="sections.showNavbar" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><Field {...props} label="Inicio" path="navigation.homeLabel" /></Grid>
        <Grid item xs={12} sm={6}><Field {...props} label="Sobre nosotros" path="navigation.aboutLabel" /></Grid>
        <Grid item xs={12} sm={6}><Field {...props} label="Productos o servicios" path="navigation.productsLabel" /></Grid>
        <Grid item xs={12} sm={6}><Field {...props} label="Contacto" path="navigation.contactLabel" /></Grid>
      </Grid>
      <Stack>
        <VisibilitySwitch {...props} label="Mostrar enlace Sobre nosotros" path="navigation.showAboutLink" />
        <VisibilitySwitch {...props} label="Mostrar enlace Productos" path="navigation.showProductsLink" />
        <VisibilitySwitch {...props} label="Mostrar enlace Contacto" path="navigation.showContactLink" />
      </Stack>
    </Stack>
  );
}

function FooterFields(props) {
  return (
    <Stack spacing={2}>
      <VisibilitySwitch {...props} label="Mostrar pie de página" path="sections.showFooter" />
      <VisibilitySwitch {...props} label="Mostrar logo" path="footer.showLogo" />
      <Field {...props} label="Descripción corta" path="footer.tagline" multiline minRows={2} />
      <Field {...props} label="Derechos de autor" path="footer.copyright" helperText="Usá {businessName} para insertar el nombre del negocio." />
    </Stack>
  );
}

export default function LandingSectionDialog({
  open,
  activeSection = "hero",
  form,
  productsEnabled = true,
  saving = false,
  onClose,
  onSave,
  onOpenSettings,
}) {
  const [draft, setDraft] = React.useState(() => clone(form || {}));
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setDraft(clone(form || {}));
      setError("");
    }
  }, [form, open, activeSection]);

  const change = (path, value) => setDraft((current) => setAtPath(current, path, value));

  const save = async () => {
    try {
      setError("");
      await onSave?.(draft);
      onClose?.();
    } catch (err) {
      setError(err?.message || "No se pudo guardar la sección");
    }
  };

  const commonProps = { form: draft, onChange: change };
  let fields = <HeroFields {...commonProps} />;

  if (activeSection === "identity") fields = <IdentityFields {...commonProps} />;
  else if (activeSection === "navbar") fields = <NavbarFields {...commonProps} />;
  else if (activeSection === "hero") fields = <HeroFields {...commonProps} />;
  else if (["featured", "featuredProducts", "products", "categories", "brands"].includes(activeSection)) {
    fields = productsEnabled
      ? <CatalogFields activeSection={activeSection} {...commonProps} />
      : <Alert severity="info">Este proyecto no tiene habilitado el módulo de productos o servicios.</Alert>;
  } else if (activeSection === "spotlight") fields = <SpotlightFields {...commonProps} />;
  else if (activeSection === "about") fields = <AboutFields {...commonProps} />;
  else if (activeSection === "highlights") fields = <HighlightsFields {...commonProps} />;
  else if (["contact", "floatingWhatsapp"].includes(activeSection)) fields = <ContactFields {...commonProps} />;
  else if (activeSection === "footer") fields = <FooterFields {...commonProps} />;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 950, color: "#19242F" }}>
        {SECTION_LABELS[activeSection] || "Editar sección"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.6)" }}>
            Los cambios se guardan en la configuración real del proyecto y se reflejan en la página pública.
          </Typography>
          {fields}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button
          onClick={() => onOpenSettings?.(activeSection === "identity" ? 0 : 1)}
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 850 }}
        >
          Ajustes avanzados
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A" }}>
            {saving ? "Guardando…" : "Guardar sección"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
